import numbro from 'numbro';
import React from 'react';
import { useSet } from 'react-use';
import styled from 'styled-components';
import { LinkButton } from '../../../CommonComponents/Button';
import Icon from '../../../CommonComponents/Icon';
import Table from '../../../CommonComponents/Table';

const StyledTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;

  ${Table.Row} + ${Table.Row} {
    border-top: 1px solid #CCCCCC;
  }
`;

interface RiskProfilePosition {
  longName: string;
  riskCode: string;
  amount: number;
  allocationPercent: number;
  analysis: { riskScoreImageUri: string };
}

interface RiskProfileAccount {
  name: string;
  riskCode: string;
  total: number;
  allocationPercent: number;
  analysis: { riskScoreImageUri: string };
  allocations: RiskProfilePosition[];
}

interface Props {
  accounts: RiskProfileAccount[];
}

export const RiskProfileDetailTable: React.FC<Props> = ({ accounts }) => {
  const [, { has: isAccountExpanded, toggle: toggleAccountExpansion }] = useSet<number>();

  return (
    <StyledTable>
      <Table.Header>
        <Table.Row>
          <Table.Cell>Account</Table.Cell>
          <Table.Cell style={{ width: 60 }} />
          <Table.RightAlignedCell style={{ width: 150 }}>Market Value</Table.RightAlignedCell>
          <Table.RightAlignedCell style={{ width: 150 }}>Allocation</Table.RightAlignedCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {accounts.map((account, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>
            <Table.Row>
              <Table.Cell>
                {account.allocations.length > 0 ? (
                  <LinkButton style={{ fontWeight: 600 }} onClick={() => toggleAccountExpansion(i)}>
                    {account.name}

                    <Icon
                      style={{ marginLeft: 8 }}
                      className={`fas fa-chevron-${isAccountExpanded(i) ? 'down' : 'right'} `}
                    />
                  </LinkButton>
                ) : (
                  <span style={{ fontWeight: 600 }}>{account.name}</span>
                )}
              </Table.Cell>
              <Table.CenterAlignedCell>
                <img
                  style={{ width: 50 }}
                  src={account.analysis.riskScoreImageUri}
                  alt="Group Risk"
                />
              </Table.CenterAlignedCell>
              <Table.RightAlignedCell>
                {numbro(account.total).formatCurrency({
                  thousandSeparated: true,
                  mantissa: 2,
                })}
              </Table.RightAlignedCell>
              <Table.RightAlignedCell>
                {numbro(account.allocationPercent).format({ output: 'percent', mantissa: 2 })}
              </Table.RightAlignedCell>
            </Table.Row>
            {isAccountExpanded(i) &&
              account.allocations.map(position => (
                <Table.Row>
                  <Table.Cell style={{ paddingLeft: 48 }}>{position.longName}</Table.Cell>
                  <Table.CenterAlignedCell>
                    <img
                      style={{ width: 40 }}
                      src={position.analysis.riskScoreImageUri}
                      alt="Group Risk"
                    />
                  </Table.CenterAlignedCell>
                  <Table.RightAlignedCell>
                    {numbro(position.amount).formatCurrency({
                      thousandSeparated: true,
                      mantissa: 2,
                    })}
                  </Table.RightAlignedCell>
                  <Table.RightAlignedCell>
                    {numbro(position.allocationPercent).format({ output: 'percent', mantissa: 2 })}
                  </Table.RightAlignedCell>
                </Table.Row>
              ))}
          </React.Fragment>
        ))}
      </Table.Body>
    </StyledTable>
  );
};
